<template>
  <div class="formulario-modelo">
    <h1>Estructura del Modelo</h1>
    <div class="divider"></div>

    <div v-if="loading" class="info">Cargando modelo...</div>
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button class="btn guardar" @click="cargar">Reintentar</button>
    </div>

    <form v-else-if="modelo" class="modelo-form" @submit.prevent>
      <!-- Nombre y estado del modelo -->
      <h2 class="modelo-nombre">{{ modelo.nombre }}</h2>

      <div class="fila">
        <label>Estado</label>
        <span :class="['estado', estadoClass(modelo.estado)]">{{ modelo.estado }}</span>
      </div>

      <!-- Nombre editable del modelo -->
      <div class="fila">
        <label>Nombre del Modelo</label>
        <input
          v-model="nombreEdit"
          type="text"
          class="form-control"
          :disabled="!editMode || saving || modelo.estado !== 'edicion'"
          :class="{ editable: editMode }"
        />
      </div>

      <!-- Niveles del modelo -->
      <div class="fila">
        <label>Niveles</label>
        <input
          v-model="nivelesString"
          type="text"
          class="form-control"
          disabled
        />
      </div>

      <!-- Fecha de creación del modelo -->
      <div class="fila">
        <label>Fecha de creación</label>
        <output>{{ formatFecha(modelo.fecha_creacion) }}</output>
      </div>

      <!-- Vigencia normativa del modelo (rango de años) -->
      <div class="fila">
        <label>Vigencia</label>
        <output>{{ vigenciaTexto }}</output>
      </div>

      <!-- Acuerdo que adopta el modelo -->
      <div class="fila">
        <label>Acuerdo</label>
        <output>{{ acuerdoEdit || '-' }}</output>
      </div>

      <!-- Escala de calificación -->
      <div class="fila">
        <label>Escala de calificación</label>
        <output>{{ escalaMaxEdit === 10 ? '1 a 10' : '0 a 5' }}</output>
      </div>

      <!-- Sección de edición -->
      <div v-if="editMode" class="edit-section">
        <div class="divider"></div>
        <h3>Datos modificables</h3>

        <!-- Nombre editable del modelo -->
        <div class="form-group">
          <label for="nombreEdit">Nombre del modelo</label>
          <input
            id="nombreEdit"
            v-model="nombreEdit"
            type="text"
            class="form-control"
            :disabled="saving || modelo.estado !== 'edicion'"
          />
        </div>

        <!-- Fecha de creación editable -->
        <div class="form-group">
          <label for="fechaEdit">Fecha de creación</label>
          <input
            id="fechaEdit"
            type="date"
            v-model="fechaEdit"
            :disabled="saving"
            class="fecha-edit-input"
          />
        </div>

        <!-- Vigencia editable (rango de años) -->
        <div class="form-group">
          <label for="vigenciaInicio">Vigencia del modelo</label>
          <div class="rango-vigencia">
            <input
              id="vigenciaInicio"
              type="number"
              min="1900"
              max="2200"
              step="1"
              placeholder="2020"
              v-model.number="vigenciaInicioEdit"
              :disabled="saving"
            />
            <span class="rango-separador">—</span>
            <input
              id="vigenciaFin"
              type="number"
              min="1900"
              max="2200"
              step="1"
              placeholder="2025"
              v-model.number="vigenciaFinEdit"
              :disabled="saving"
            />
          </div>
          <small class="ayuda">Años de inicio y fin de vigencia (ej. 2020-2025).</small>
        </div>

        <!-- Acuerdo editable -->
        <div class="form-group">
          <label for="acuerdoEdit">Acuerdo</label>
          <input
            id="acuerdoEdit"
            type="text"
            maxlength="150"
            placeholder="Ejemplo: Acuerdo 02 de 2005"
            v-model="acuerdoEdit"
            :disabled="saving"
          />
        </div>

        <!-- Escala de calificación (editable) -->
        <div class="form-group">
          <label>Escala de calificación</label>
          <div class="escala-toggle">
            <button type="button" :class="['escala-opcion', escalaMaxEdit === 5 ? 'activa' : '']" :disabled="saving" @click="escalaMaxEdit = 5">0 a 5</button>
            <button type="button" :class="['escala-opcion', escalaMaxEdit === 10 ? 'activa' : '']" :disabled="saving" @click="escalaMaxEdit = 10">1 a 10</button>
          </div>
        </div>

        <!-- Niveles editable (del modelo) -->
        <div class="form-group">
          <label for="cantidadNiveles">Niveles</label>
          <input
            id="cantidadNiveles"
            type="number"
            min="1"
            placeholder="Ejemplo: 3"
            v-model.number="cantidadNiveles"
            @input="generarNiveles"
            :disabled="saving"
          />
        </div>

        <div class="divider"></div>

        <!-- Listado de niveles -->
        <div v-if="niveles.length > 0" class="niveles-listado">
          <h3>Nombre los Niveles</h3>
          <div v-for="(nombre, index) in niveles" :key="index" class="form-group">
            <label :for="'nivel-' + index">Nivel {{ index + 1 }}</label>
            <input
              :id="'nivel-' + index"
              type="text"
              placeholder="Nombre del Nivel"
              v-model="niveles[index]"
              :disabled="saving"
            />
          </div>
        </div>
      </div>

      <!-- Botones -->
      <div class="botones">
        <template v-if="!editMode">
          <button type="button" class="btn salir" @click="volver">Salir</button>
          <button type="button" class="btn guardar" :disabled="modelo.estado !== 'edicion'" @click="irAgregarComponentes">
            Agregar {{ nivelesArray[0] ?? 'Elementos' }}
          </button>
          <button type="button" class="btn guardar" @click="irAGestionPesos">
            Ajustar Pesos
          </button>
          <button 
            type="button" 
            class="btn guardar" 
            @click="toggleEditarGuardar"
            :disabled="saving"
          >
            Modificar
          </button>
        </template>

        <!-- Cuando SÍ está en modo edición -->
        <template v-else>
           <button 
            type="button" 
            class="btn salir" 
            @click="cancelarEdicion"
            :disabled="saving"
          >
            Cancelar
          </button>
          <button 
            type="button" 
            class="btn guardar" 
            @click="guardarCambios"
            :disabled="saving"
          >
            {{ saving ? 'Guardando...' : 'Guardar' }}
          </button>
        </template>
      </div>
    </form>

    <div v-else>
      <p>No fue posible cargar el Modelo.</p>
      <div style="margin-top:1rem;"><button class="btn guardar" @click="cargar">Reintentar</button></div>
    </div>

    <MensajeFlotante :visible="mensajeVisible" :mensaje="mensajeTexto" :tipo="mensajeTipo" @cerrar="mensajeVisible = false" />
  </div>
</template>

<script setup lang="ts">
/**
 * Árbol completo del modelo en modo consulta.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `Estructura/:id_modelo/:id_version?` (EstructuraModelo) — permiso `modelos.ver`
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useModelo from '@/store/GestionModelo'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import MensajeFlotante from './MensajeFlotante.vue'
import { limpiarNiveles, formatearNivelesParaMostrar } from '@/utils/nivelesHelper'

const route = useRoute()
const router = useRouter()
const modeloStore = useModelo()
const modelo = ref<EstructuraModelo | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const nombreEdit = ref('')
const cantidadNiveles = ref(0)
const niveles = ref<string[]>([])
const idModelo = Number(route.params.id_modelo ?? NaN)
const nivelesString = computed(() => formatearNivelesParaMostrar(niveles.value))
const editMode = ref(false)
const saving = ref(false)
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')
const fechaEdit = ref('')
const escalaMaxEdit = ref<5 | 10>(5)
// Vigencia normativa (rango de años) y acuerdo: independientes de fecha_creacion.
const vigenciaInicioEdit = ref<number | null>(null)
const vigenciaFinEdit = ref<number | null>(null)
const acuerdoEdit = ref('')

const vigenciaTexto = computed(() =>
  vigenciaInicioEdit.value != null && vigenciaFinEdit.value != null
    ? `${vigenciaInicioEdit.value}-${vigenciaFinEdit.value}`
    : '-'
)

// Mismas reglas que el backend. Devuelve el mensaje de error, o null si es válido.
function validarVigencia(): string | null {
  const inicio = vigenciaInicioEdit.value
  const fin = vigenciaFinEdit.value

  if (inicio == null && fin == null) return null
  if (inicio == null || fin == null) {
    return 'La vigencia requiere el año inicial y el año final.'
  }
  if (!Number.isInteger(inicio) || !Number.isInteger(fin)) {
    return 'Los años de vigencia deben ser números enteros.'
  }
  if (inicio < 1900 || inicio > 2200 || fin < 1900 || fin > 2200) {
    return 'Los años de vigencia deben estar entre 1900 y 2200.'
  }
  if (fin < inicio) {
    return 'El año final de la vigencia no puede ser anterior al año inicial.'
  }
  return null
}

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const nivelesArray = computed(() => {
  if (!modelo.value?.niveles) return []

  if (Array.isArray(modelo.value.niveles)) {
    return modelo.value.niveles.map(n => n.trim()).filter(n => n !== '')
  }

  if (typeof modelo.value.niveles === 'string') {
    return modelo.value.niveles.split(',').map(n => n.trim()).filter(n => n !== '')
  }

  return []
})

function validarParametrosRuta(): boolean {
  if (!Number.isFinite(idModelo) || idModelo <= 0) {
    error.value = `Error en parámetros de ruta: id_modelo inválido: ${idModelo}`
    console.warn('[MostrarEstructura] Parámetros inválidos detectados:', error.value)
    return false
  }
  return true
}

async function cargar() {
  loading.value = true
  error.value = null
  try {
    const m = await modeloStore.getModeloById(idModelo)
    if (!m) throw new Error('No se encontró el modelo.')
    modelo.value = m
    nombreEdit.value = m.nombre
    fechaEdit.value = (m.fecha_creacion ?? '').substring(0, 10)
    escalaMaxEdit.value = m.escala_max ?? 5
    vigenciaInicioEdit.value = m.vigencia_inicio ?? null
    vigenciaFinEdit.value = m.vigencia_fin ?? null
    acuerdoEdit.value = m.acuerdo ?? ''
    const arrNiveles = limpiarNiveles(m.niveles)
    cantidadNiveles.value = arrNiveles.length
    niveles.value = arrNiveles
  } catch (err: any) {
    error.value = err?.message || 'Error desconocido al cargar el modelo.'
    modelo.value = null
  } finally {
    loading.value = false
  }
}

function volver() {
  router.push({ name: 'ListaVersiones' })
}

function irAgregarComponentes() {
  router.push({ name: 'ComponentesModelo', params: { id_modelo: idModelo } })
}

function estadoClass(estado?: string | null) {
  switch (estado) {
    case 'aprobado': return 'estado-aprobado'
    case 'descontinuado': return 'estado-descontinuado'
    case 'completo': return 'estado-completo'
    default: return 'estado-modificar'
  }
}

// Función para formatear fecha
function formatFecha(fecha?: string | null) {
  if (!fecha) return '-'

  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    const [yyyy, mm, dd] = fecha.split('-').map(Number)
    return new Date(yyyy, mm - 1, dd).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(fecha)) {
    const [datePart] = fecha.split('T')
    const [yyyy, mm, dd] = datePart.split('-').map(Number)

    return new Date(yyyy, mm - 1, dd).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const d = new Date(fecha)
  return isNaN(d.getTime())
    ? fecha
    : d.toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })
}

function toggleEditarGuardar() {
  if (!editMode.value) {
    editMode.value = true
    return
  }
  guardarCambios()
}

function irAGestionPesos() {
  if (!Number.isFinite(idModelo) || idModelo <= 0) {
    error.value = 'ID de modelo inválido.'
    return
  }
  router.push({ name: 'Pesos', params: { id_modelo: idModelo } })
}

function generarNiveles() {
  const nuevaCantidad = cantidadNiveles.value
  if (nuevaCantidad < 1) {
    niveles.value = []
    return
  }
  const nuevos = Array.from({ length: nuevaCantidad }, (_, i) => niveles.value[i] || '')
  niveles.value = nuevos
}

async function guardarCambios() {
  if (!modelo.value) return
  if (niveles.value.some(n => !n.trim())) {
    mostrarMensaje('Debe ingresar nombre para todos los niveles.', 'error')
    return
  }

  const errorVigencia = validarVigencia()
  if (errorVigencia) {
    mostrarMensaje(errorVigencia, 'error')
    return
  }

  saving.value = true
  try {
    // Actualizamos nombre, niveles, vigencia y acuerdo del modelo
    const modeloActualizado: EstructuraModelo = {
      ...modelo.value,
      nombre: nombreEdit.value.trim(),
      fecha_creacion: fechaEdit.value,
      escala_max: escalaMaxEdit.value,
      vigencia_inicio: vigenciaInicioEdit.value ?? null,
      vigencia_fin: vigenciaFinEdit.value ?? null,
      acuerdo: acuerdoEdit.value.trim() || null,
      niveles: niveles.value
    }
    const okModelo = await modeloStore.updateModelo(modeloActualizado)

    if (okModelo) {
      modelo.value = modeloActualizado
      mostrarMensaje('Cambios guardados correctamente.', 'positivo')
      editMode.value = false
    } else {
      mostrarMensaje('No se pudieron guardar los cambios.', 'error')
    }
  } catch (e: any) {
    mostrarMensaje('Error al guardar los cambios. Intente de nuevo.', 'error')
  } finally {
    saving.value = false
  }
}

function cancelarEdicion() {
  editMode.value = false
  if (modelo.value) {
    nombreEdit.value = modelo.value.nombre
    fechaEdit.value = (modelo.value.fecha_creacion ?? '').substring(0, 10)
    escalaMaxEdit.value = modelo.value.escala_max ?? 5
    vigenciaInicioEdit.value = modelo.value.vigencia_inicio ?? null
    vigenciaFinEdit.value = modelo.value.vigencia_fin ?? null
    acuerdoEdit.value = modelo.value.acuerdo ?? ''
    const arrNiveles = limpiarNiveles(modelo.value.niveles)
    cantidadNiveles.value = arrNiveles.length
    niveles.value = arrNiveles
  }
}

onMounted(() => {
  if (!validarParametrosRuta()) return
  cargar()
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  width: 95%;
  max-height: 100%;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px auto;
  font-family: var(--font-family);
  max-width: 900px;
}

h1 {
  margin-bottom: 1rem;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: var(--line-height-tight);
}

.divider { 
  border-bottom: 1px solid #949393; 
  margin: 12px 0 24px; 
}

.fila { 
  display: flex; 
  align-items: center; 
  gap: 1rem; 
  margin-bottom: 1rem; 
}

.fila > label { 
  width: 220px; 
  margin: 0; 
  font-weight: 600; 
}

.fila > output { 
  flex: 1; 
  padding: 0.5rem 0.75rem; 
  background: #fbfbfb; 
  border-radius: 6px; 
  border: 1px solid #ececec; 
}

.fila > input.form-control {
  flex: 1;
  padding: 0.5rem 0.75rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #ececec;
  font-size: 1rem;
  font-family: inherit;
  color: inherit;
}

.botones { 
  display: flex; 
  justify-content: flex-end; 
  gap: 1rem; 
  margin-top: 1.5rem; 
}

.btn {
  padding: 0.6rem 1rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: var(--font-family);
}

.btn.salir { 
  background-color: white; 
  border: 1px solid #ccc; 
  color: #333; 
}

.btn.guardar { 
  background-color: #eea222; 
  color: black; 
}

.btn.guardar:disabled { 
  opacity: 0.6; 
  cursor: not-allowed;
}

.info { 
  padding: 1rem 0; 
}

.error { 
  color: #b00020; 
  padding: 1rem 0; 
}

.form-control.editable {
  border: 2px solid #0e69a0;
  background-color: #f0f8ff;
}

.form-control.editable:focus {
  border-color: #084c74;
  box-shadow: 0 0 6px rgba(14, 105, 160, 0.6);
}

.edit-section {
  margin-top: 1.5rem;
}

.edit-section h3 {
  margin-bottom: 1rem;
  font-size: 1.2rem;
  color: #0e69a0;
}

.form-group {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 6px;
}

.form-group input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ececec;
  border-radius: 6px;
  font-size: 1rem;
  background: #fff;
}

.niveles-listado {
  margin-top: 1rem;
}

.niveles-listado h3 {
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
  color: #333;
}

.btn.guardar:hover { 
  background-color: #0e69a0; 
  color: white; 
}

.btn.btn.salir:hover {
  background-color: #af0e09;
  color: white;
}

.fecha-edit-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ececec;
  border-radius: 6px;
  font-size: 1rem;
  background: #fff;
  font-family: inherit;
  cursor: pointer;
  max-width: 220px;
}

.fecha-edit-input:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.rango-vigencia {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.rango-vigencia input {
  max-width: 120px;
}

.ayuda {
  margin-top: 6px;
  font-size: 0.75rem;
  color: #777;
}

.rango-separador {
  color: #777;
  font-weight: 600;
}

.escala-toggle {
  display: flex;
  gap: 0;
  border: 1px solid #e6e5e5;
  border-radius: 6px;
  overflow: hidden;
  width: fit-content;
}

.escala-opcion {
  padding: 0.45rem 1.25rem;
  font-size: 0.85rem;
  font-family: var(--font-family);
  font-weight: 500;
  background-color: #f9f9f9;
  color: #555;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.escala-opcion:not(:last-child) {
  border-right: 1px solid #e6e5e5;
}

.escala-opcion.activa {
  background-color: #0e69a0;
  color: white;
  font-weight: 600;
}

.escala-opcion:not(.activa):hover:not(:disabled) {
  background-color: #eef4fa;
  color: #0e69a0;
}

.escala-opcion:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
